import React, { useEffect, useId, useRef } from "react";
import { AlertTriangle, CheckCircle2, AlertCircle } from "lucide-react";

export interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: "danger" | "primary" | "warning";
  onConfirm: () => void;
  onCancel: () => void;
}

const confirmVariantClasses: Record<NonNullable<ConfirmModalProps["confirmVariant"]>, string> = {
  danger: "bg-red-600 hover:bg-red-700 text-white",
  primary: "bg-emerald-600 hover:bg-emerald-700 text-white",
  warning: "bg-amber-600 hover:bg-amber-700 text-white",
};

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant = "danger",
  onConfirm,
  onCancel,
}) => {
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const variant = confirmVariant ?? "danger";

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    cancelRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onCancel]);

  if (!isOpen) {
    return null;
  }

  const icon =
    variant === "danger" ? (
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-500/15 text-red-400">
        <AlertTriangle className="h-6 w-6" aria-hidden />
      </div>
    ) : variant === "primary" ? (
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
        <CheckCircle2 className="h-6 w-6" aria-hidden />
      </div>
    ) : (
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/15 text-amber-400">
        <AlertCircle className="h-6 w-6" aria-hidden />
      </div>
    );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm iw-modal-backdrop-in"
      role="presentation"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="iw-modal-panel-in w-full max-w-md rounded-xl border border-slate-700 bg-slate-800 p-6 shadow-2xl transition-transform"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">{icon}</div>
        <h2 id={titleId} className="text-center text-lg font-semibold text-white">
          {title}
        </h2>
        <p className="mt-2 text-center text-sm text-slate-300">{message}</p>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            ref={cancelRef}
            type="button"
            className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-600"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${confirmVariantClasses[variant]}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
